import { VerifiedCustody } from "@verified-network/verified-custody";
import { useEffect, useRef } from "react";
import { chainDetails } from "../utils/config";
import {
  sendCoSignerInvitation,
  sendCreatorConfirmation,
  sendCreatorInitiation,
  sendCreatorSigned,
  sendCreatorCompleted,
  sendCreatorAcceptance,
  sendCreatorRejection,
  sendOTPVerification,
  checkOTPVerification,
  sendCreatorAuthChanged,
} from "../utils/helpers/verifiedCustody.js";

export const useHandleWalletRedirections = (
  mobileScreen,
  setVerifiedWalletDetails,
  setCustodyWalletDetails,
  setPopupContentPosition,
  setPopupContent,
  setShowPopup,
  setPageAction,
) => {
  const getPkHandledRef = useRef(false);
  const signRecoveryHandledRef = useRef(false);
  const completeRecoveryHandledRef = useRef(false);

  useEffect(() => {
    const windowLoadHandler = () => {
      const myVault = JSON.parse(localStorage.getItem("VW:myVault"));
      const myCustody = JSON.parse(localStorage.getItem("vHexT"));

      if (!myVault?.address && myCustody?.address) {
        setCustodyWalletDetails(myCustody);
      } else if (
        myVault &&
        myVault?.pk?.startsWith("0x") &&
        myVault?.address?.length > 0
      ) {
        setVerifiedWalletDetails(myVault);
      }
    };

    const vaultConnectedHandler = (evnt) => {
      const verifiedWallet = evnt.detail.value;
      if (mobileScreen) {
        const myVault = JSON.parse(localStorage.getItem("myVault"));
        if (myVault?.hashedVaultId) {
          setVerifiedWalletDetails({
            ...verifiedWallet,
            hashedVaultId: myVault?.hashedVaultId,
          });
          localStorage.setItem(
            "VW:myVault",
            JSON.stringify({
              ...verifiedWallet,
              hashedVaultId: myVault?.hashedVaultId,
            }),
          );
        }
      } else {
        setVerifiedWalletDetails(verifiedWallet);
        localStorage.setItem("VW:myVault", JSON.stringify(verifiedWallet));
      }
    };

    const vaultDataHandler = (evnt) => {
      const vaultData = evnt.detail.value;
      if (mobileScreen) {
        const myVault = JSON.parse(localStorage.getItem("myVault"));
        if (myVault?.hashedVaultId) {
          setVerifiedWalletDetails({
            ...vaultData,
            hashedVaultId: myVault?.hashedVaultId,
          });
          localStorage.setItem(
            "VW:myVault",
            JSON.stringify({
              ...vaultData,
              hashedVaultId: myVault?.hashedVaultId,
            }),
          );
        }
      } else {
        setVerifiedWalletDetails(vaultData);
        localStorage.setItem("VW:myVault", JSON.stringify(vaultData));
      }
    };

    const getPkHandler = (evnt) => {
      if (getPkHandledRef.current) return;
      getPkHandledRef.current = true;

      const vaultDetails = evnt.detail.value;

      setPopupContentPosition("center");
      setPopupContent(
        <div
          style={
            mobileScreen
              ? { maxWidth: "350px", maxHeight: "650px", padding: "0" }
              : { padding: "0", maxHeight: "650px", maxWidth: "400px" }
          }
          className="verify-kyc-popup"
        >
          <VerifiedCustody
            action="getPk"
            origin={window.location.host}
            title={"Verified Network"}
            reqVaultData={
              vaultDetails?.vaultData
                ? {
                    vaultId:
                      vaultDetails?.vaultData?.vId ||
                      vaultDetails?.vaultData?.vaultId,
                    hashedVaultId: vaultDetails?.vaultData?.hashedVaultId,
                    hashedVaultPin:
                      vaultDetails?.vaultData?.vPh ||
                      vaultDetails?.vaultData?.hashedVaultPin,
                    coSignerId: vaultDetails?.vaultData?.cId,
                  }
                : null
            }
            helperFunctions={{
              sendCoSignerInvitation,
              sendCreatorConfirmation,
              sendCreatorInitiation,
              sendCreatorSigned,
              sendCreatorCompleted,
              sendCreatorAcceptance,
              sendCreatorRejection,
              sendOTPVerification,
              checkOTPVerification,
              sendCreatorAuthChanged,
            }}
          />
        </div>,
      );
      setShowPopup(true);
    };

    const signRecoveryHandler = (evnt) => {
      if (signRecoveryHandledRef.current) return;
      signRecoveryHandledRef.current = true;

      const vaultDetails = evnt.detail.value;

      setPopupContentPosition("center");
      setPopupContent(
        <div
          style={
            mobileScreen
              ? { maxWidth: "350px", maxHeight: "650px", padding: "0" }
              : { padding: "0", maxHeight: "650px", maxWidth: "400px" }
          }
          className="verify-kyc-popup"
        >
          <VerifiedCustody
            action="signRecovery"
            origin={window.location.host}
            title={"Verified Network"}
            chainId={
              chainDetails[0].verifiedWalletChainId || chainDetails[0].chainId
            }
            reqVaultData={
              vaultDetails?.vaultData
                ? {
                    vId:
                      vaultDetails?.vaultData?.vId ||
                      vaultDetails?.vaultData?.vaultId,
                    hashedVaultId: vaultDetails?.vaultData?.hashedVaultId,
                    vPh:
                      vaultDetails?.vaultData?.vPh ||
                      vaultDetails?.vaultData?.hashedVaultPin,
                    cId: vaultDetails?.vaultData?.cId,
                  }
                : null
            }
            txData={
              vaultDetails?.txData
                ? {
                    id: vaultDetails?.txData?.id,
                  }
                : null
            }
            helperFunctions={{
              sendCoSignerInvitation,
              sendCreatorConfirmation,
              sendCreatorInitiation,
              sendCreatorSigned,
              sendCreatorCompleted,
              sendCreatorAcceptance,
              sendCreatorRejection,
              sendOTPVerification,
              checkOTPVerification,
              sendCreatorAuthChanged,
            }}
          />
        </div>,
      );
      setShowPopup(true);
    };

    const completeRecoveryHandler = (evnt) => {
      if (completeRecoveryHandledRef.current) return;
      completeRecoveryHandledRef.current = true;

      const vaultDetails = evnt.detail.value;

      setPopupContentPosition("center");
      setPopupContent(
        <div
          style={
            mobileScreen
              ? { maxWidth: "350px", maxHeight: "650px", padding: "0" }
              : { padding: "0", maxHeight: "650px", maxWidth: "400px" }
          }
          className="verify-kyc-popup"
        >
          <VerifiedCustody
            action="completeRecovery"
            origin={window.location.host}
            title={"Verified Network"}
            chainId={
              chainDetails[0].verifiedWalletChainId || chainDetails[0].chainId
            }
            reqVaultData={
              vaultDetails?.vaultData
                ? {
                    vId:
                      vaultDetails?.vaultData?.vId ||
                      vaultDetails?.vaultData?.vaultId,
                    hashedVaultId: vaultDetails?.vaultData?.hashedVaultId,
                    vPh:
                      vaultDetails?.vaultData?.vPh ||
                      vaultDetails?.vaultData?.hashedVaultPin,
                    cId: vaultDetails?.vaultData?.cId,
                  }
                : null
            }
            txData={
              vaultDetails?.txData
                ? {
                    id: vaultDetails?.txData?.id,
                  }
                : null
            }
            helperFunctions={{
              sendCoSignerInvitation,
              sendCreatorConfirmation,
              sendCreatorInitiation,
              sendCreatorSigned,
              sendCreatorCompleted,
              sendCreatorAcceptance,
              sendCreatorRejection,
              sendOTPVerification,
              checkOTPVerification,
              sendCreatorAuthChanged,
            }}
          />
        </div>,
      );
      setShowPopup(true);
    };

    const closePopupHandler = () => {
      setShowPopup(false);
      setPopupContentPosition("center");
      setPopupContent(null);
      setPageAction("");

      getPkHandledRef.current = false;
      signRecoveryHandledRef.current = false;
      completeRecoveryHandledRef.current = false;
    };

    const connectWalletHandler = (evnt) => {
      setPopupContentPosition("center");
      setPopupContent(
        <div
          style={
            mobileScreen
              ? { maxWidth: "350px", maxHeight: "650px", padding: "0" }
              : { padding: "0", maxHeight: "650px", maxWidth: "400px" }
          }
          className="verify-kyc-popup"
        >
          <VerifiedCustody
            action="connect_wallet"
            origin={window.location.host}
            title={"Verified Network"}
            helperFunctions={{
              sendCoSignerInvitation,
              sendCreatorConfirmation,
              sendCreatorInitiation,
              sendCreatorSigned,
              sendCreatorCompleted,
              sendCreatorAcceptance,
              sendCreatorRejection,
              sendOTPVerification,
              checkOTPVerification,
              sendCreatorAuthChanged,
            }}
          />
        </div>,
      );
      setShowPopup(true);
    };

    // Event Listeners
    window.addEventListener("load", windowLoadHandler);
    window.addEventListener("vaultConnected", vaultConnectedHandler);
    window.addEventListener("vaultData", vaultDataHandler);
    window.addEventListener("getPk", getPkHandler);
    window.addEventListener("signRecovery", signRecoveryHandler);
    window.addEventListener("completeRecovery", completeRecoveryHandler);
    window.addEventListener("closePopup", closePopupHandler);
    window.addEventListener("connect_wallet", connectWalletHandler);

    return () => {
      window.removeEventListener("load", windowLoadHandler);
      window.removeEventListener("vaultConnected", vaultConnectedHandler);
      window.removeEventListener("vaultData", vaultDataHandler);
      window.removeEventListener("getPk", getPkHandler);
      window.removeEventListener("signRecovery", signRecoveryHandler);
      window.removeEventListener("completeRecovery", completeRecoveryHandler);
      window.removeEventListener("closePopup", closePopupHandler);
      window.removeEventListener("connect_wallet", connectWalletHandler);
    };
  }, [
    mobileScreen,
    setCustodyWalletDetails,
    setPageAction,
    setPopupContent,
    setPopupContentPosition,
    setShowPopup,
    setVerifiedWalletDetails,
  ]);
};
